import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Callout,
  Card,
  DefinitionList,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  PageIntro,
  StateBadge,
  TransactionBadge,
  formatDateTime,
  formatNumber,
  formatRelative,
  titleCase,
  useToast,
} from "../components/ui.jsx";
import { IconChevronLeft, IconKey, IconRefresh } from "../components/icons.jsx";
import { kindLabel } from "./Dashboard.jsx";

export default function DeviceDetail() {
  const { number } = useParams();
  const toast = useToast();
  const [tab, setTab] = useState("overview");
  const [confirmRotate, setConfirmRotate] = useState(false);

  const device = useAsync(() => api.getDevice(number), [number]);
  const channels = useAsync(() => api.deviceChannels(number), [number]);
  const commands = useAsync(() => api.deviceCommands(number, 25), [number]);
  const reading = useAsync(
    () => api.latestReading(number, 0).catch(() => null),
    [number]
  );

  if (device.loading) return <Loading label="Loading device" />;
  if (device.error) return <ErrorState error={device.error} onRetry={device.reload} />;

  const d = device.data;
  const isGateway = d.Kind === "NOVALINK";

  async function rotateKey() {
    try {
      await api.rotateDeviceKey(number);
      setConfirmRotate(false);
      device.reload();
      toast.success(
        "Token key rotated",
        "Any token issued under the previous revision will now be rejected by the device."
      );
    } catch (err) {
      toast.error("Could not rotate the key", err.message);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Link className="btn btn-ghost btn-sm" to="/devices">
          <IconChevronLeft /> All devices
        </Link>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 className="mono" style={{ fontSize: 24 }}>
            {d.Number}
          </h1>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <StateBadge state={d.State} />
            <span className="hint">
              {kindLabel(d.Kind)} · {d.ModelCode} ·{" "}
              {d.ChannelCount > 1 ? `${d.ChannelCount}-in-1` : "single channel"}
            </span>
          </div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => device.reload()}>
            <IconRefresh /> Refresh
          </button>
          <Link className="btn btn-primary btn-sm" to={`/commands?device=${d.Number}`}>
            Send a command
          </Link>
        </div>
      </div>

      <div className="tabs">
        {[
          ["overview", "Overview"],
          ["channels", isGateway ? "Attached devices" : "Connections"],
          ["topology", "Topology"],
          ["commands", "Command history"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`tab${tab === key ? " active" : ""}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-2">
          <Card title="Identity" hint="The two identifiers are separate and must not be confused">
            <DefinitionList
              items={[
                ["Customer number", <span className="mono">{d.Number}</span>],
                ["Manufacturer serial", <span className="mono">{d.Serial}</span>],
                ["Product kind", kindLabel(d.Kind)],
                ["Model", d.ModelCode],
                ["Tenant", d.TenantAlias || null],
                [
                  "Reports through",
                  d.UplinkNumber ? (
                    <Link className="mono" to={`/devices/${d.UplinkNumber}`}>
                      {d.UplinkNumber}
                    </Link>
                  ) : null,
                ],
                [
                  "Draws power from",
                  d.SupplyNumber ? (
                    <Link className="mono" to={`/devices/${d.SupplyNumber}`}>
                      {d.SupplyNumber}
                    </Link>
                  ) : null,
                ],
                ["Time zone", d.TimeZone],
                ["Firmware", d.FirmwareVersion || null],
              ]}
            />
            <p className="hint" style={{ marginTop: 12 }}>
              The customer number feeds token key derivation. The serial is for inventory
              and firmware targeting only.
            </p>
          </Card>

          <Card title="Service" hint="Commissioned settings and current status">
            <DefinitionList
              items={[
                ["Lifecycle state", <StateBadge state={d.State} />],
                ["Power limit", d.PowerLimitWatts ? `${formatNumber(d.PowerLimitWatts, 0)} W` : "Unlimited"],
                ["Phases", d.Phases === 3 ? "Three phase" : "Single phase"],
                ["Key revision", `#${d.KeyRevision}`],
                ["Subscription expiry", formatDateTime(d.SubscriptionExpiry)],
                ["Last seen", d.LastSeenAt ? `${formatRelative(d.LastSeenAt)} (${formatDateTime(d.LastSeenAt)})` : null],
                ["Registered", formatDateTime(d.CreatedAt)],
              ]}
            />
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmRotate(true)}>
                <IconKey /> Rotate token key
              </button>
            </div>
          </Card>

          {!isGateway && (
            <Card title="Latest reading" hint="Reported by the device itself, never recomputed">
              {reading.loading ? (
                <Loading />
              ) : !reading.data ? (
                <Empty title="No readings yet">
                  This device has not reported any metering data.
                </Empty>
              ) : (
                <DefinitionList
                  items={[
                    ["Recorded at", formatDateTime(reading.data.RecordedAt)],
                    ["Active energy", `${formatNumber(reading.data.ActiveEnergy)} kWh`],
                    ["Active power", `${formatNumber(reading.data.ActivePower)} W`],
                    ["Voltage", `${formatNumber(reading.data.Voltage, 1)} V`],
                    ["Current", `${formatNumber(reading.data.Current, 2)} A`],
                    ["Power factor", formatNumber(reading.data.PowerFactor, 2)],
                    [
                      "Device balance",
                      reading.data.Balance !== null && reading.data.Balance !== undefined
                        ? formatNumber(reading.data.Balance)
                        : null,
                    ],
                  ]}
                />
              )}
            </Card>
          )}
        </div>
      )}

      {tab === "channels" && (
        <ChannelsTab device={d} channels={channels} isGateway={isGateway} number={number} />
      )}

      {tab === "topology" && <TopologyTab device={d} onChanged={device.reload} />}

      {tab === "commands" && (
        <Card
          title="Command history"
          hint="Newest first. A command stays open until the device acknowledges it."
        >
          {commands.loading ? (
            <Loading />
          ) : (commands.data ?? []).length === 0 ? (
            <Empty title="No commands sent">
              Commands issued to this device will be listed here with their outcome.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ border: "none" }}>
              <table>
                <thead>
                  <tr>
                    <th>Command</th>
                    <th>Channel</th>
                    <th>State</th>
                    <th>Requested</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {commands.data.map((t) => (
                    <tr key={t.ID}>
                      <td className="mono">{t.CommandType}</td>
                      <td>{t.ChannelIndex || "Whole unit"}</td>
                      <td>
                        <TransactionBadge state={t.State} />
                      </td>
                      <td className="hint">{formatRelative(t.CreatedAt)}</td>
                      <td className="hint">{t.ResultMessage || t.ResultCode || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {confirmRotate && (
        <Modal
          title="Rotate the token key?"
          hint="This takes effect on the device immediately."
          onClose={() => setConfirmRotate(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setConfirmRotate(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={rotateKey}>
                Rotate the key
              </button>
            </>
          }
        >
          <Callout kind="warning">
            Every token already issued for this device under key revision #{d.KeyRevision}{" "}
            will stop working. Any token a customer is holding but has not yet entered
            becomes worthless and must be reissued.
          </Callout>
          <p>
            Rotate the key when you believe the device's credit history has been
            compromised. Otherwise leave it alone.
          </p>
        </Modal>
      )}
    </>
  );
}

// Kinds that can feed each product family. Mirrors supplyParents in
// internal/domain/device.go; the server is authoritative and rejects anything
// else, but stating it here lets the console offer only valid choices.
const SUPPLY_PARENTS = {
  BRANCH: ["NODENET", "NODEFLEX"],
  BRANCH_PLUS: ["POWERSHARE"],
};

/**
 * TopologyTab separates the two relationships a device has with the network.
 *
 * They are almost always different boxes, and conflating them is why a legacy
 * registry could say who a meter talked to but not who fed it. During an
 * outage only the second question matters.
 */
function TopologyTab({ device, onChanged }) {
  const toast = useToast();
  const [linking, setLinking] = useState(null);

  const allowed = SUPPLY_PARENTS[device.Kind] ?? [];
  const supplies = ["NODENET", "NODEFLEX", "POWERSHARE"].includes(device.Kind);
  const isGateway = device.Kind === "NOVALINK";

  const supplied = useAsync(
    () => (supplies ? api.deviceSupplied(device.Number) : Promise.resolve([])),
    [device.Number, supplies]
  );
  const children = useAsync(
    () => (isGateway ? api.deviceChildren(device.Number) : Promise.resolve([])),
    [device.Number, isGateway]
  );

  async function detach() {
    try {
      await api.detachDevice(device.Number);
      toast.success("Detached from its gateway");
      onChanged();
    } catch (err) {
      toast.error("Could not detach", err.message);
    }
  }

  async function unsupply() {
    try {
      await api.unsupplyDevice(device.Number);
      toast.success("Supply link removed");
      onChanged();
    } catch (err) {
      toast.error("Could not remove the supply link", err.message);
    }
  }

  return (
    <>
      <div className="grid grid-2">
        <Card
          title="Communications"
          hint="Which gateway carries this device's traffic"
        >
          {device.UplinkNumber ? (
            <>
              <DefinitionList
                items={[
                  [
                    "Reports through",
                    <Link className="mono" to={`/devices/${device.UplinkNumber}`}>
                      {device.UplinkNumber}
                    </Link>,
                  ],
                ]}
              />
              <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setLinking("uplink")}>
                  Change gateway
                </button>
                <button className="btn btn-ghost btn-sm" onClick={detach}>
                  Detach
                </button>
              </div>
            </>
          ) : (
            <Empty title="Talks to the head-end directly">
              {isGateway
                ? "A gateway is addressed in its own right and has no uplink."
                : "Commands cannot be routed to this device until it is attached to a gateway."}
              {!isGateway && (
                <div style={{ marginTop: 14 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => setLinking("uplink")}>
                    Attach to a gateway
                  </button>
                </div>
              )}
            </Empty>
          )}
        </Card>

        <Card title="Supply" hint="Which device this one draws its energy from">
          {allowed.length === 0 ? (
            <Empty title="A source in its own right">
              {isGateway
                ? "A NovaLink carries data, not energy."
                : `A ${kindLabel(device.Kind)} generates or distributes power rather than drawing it from something upstream.`}
            </Empty>
          ) : device.SupplyNumber ? (
            <>
              <DefinitionList
                items={[
                  [
                    "Fed by",
                    <Link className="mono" to={`/devices/${device.SupplyNumber}`}>
                      {device.SupplyNumber}
                    </Link>,
                  ],
                ]}
              />
              <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setLinking("supply")}>
                  Move to another {allowed.length === 1 ? kindLabel(allowed[0]) : "source"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={unsupply}>
                  Disconnect
                </button>
              </div>
            </>
          ) : (
            <Empty title="Nothing recorded as feeding this device">
              A {kindLabel(device.Kind)} is not standalone. Record the{" "}
              {allowed.map(kindLabel).join(" or ")} that feeds it. It cannot be put into
              service without one.
              <div style={{ marginTop: 14 }}>
                <button className="btn btn-primary btn-sm" onClick={() => setLinking("supply")}>
                  Record its supply
                </button>
              </div>
            </Empty>
          )}
        </Card>
      </div>

      {supplies && (
        <Card
          title="Service points it feeds"
          hint="These lose power if this device fails"
          style={{ marginTop: 16 }}
        >
          <LinkedDevices state={supplied} emptyTitle="Feeding nothing yet" />
        </Card>
      )}

      {isGateway && (
        <Card
          title="Devices reporting through it"
          hint="Their commands are routed through this gateway"
          style={{ marginTop: 16 }}
        >
          <LinkedDevices state={children} emptyTitle="Nothing attached" />
        </Card>
      )}

      {linking && (
        <LinkDevice
          device={device}
          relation={linking}
          allowed={allowed}
          onClose={() => setLinking(null)}
          onLinked={() => {
            setLinking(null);
            onChanged();
            supplied.reload();
            children.reload();
          }}
        />
      )}
    </>
  );
}

function LinkedDevices({ state, emptyTitle }) {
  if (state.loading) return <Loading />;
  const list = state.data ?? [];
  if (list.length === 0) return <Empty title={emptyTitle} />;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Number</th>
            <th>Kind</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.Number}>
              <td>
                <Link className="mono" to={`/devices/${c.Number}`}>
                  {c.Number}
                </Link>
              </td>
              <td>{kindLabel(c.Kind)}</td>
              <td>
                <StateBadge state={c.State} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** LinkDevice records either relationship, offering only valid candidates. */
function LinkDevice({ device, relation, allowed, onClose, onLinked }) {
  const isSupply = relation === "supply";
  const kinds = isSupply ? allowed : ["NOVALINK"];
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const candidates = useAsync(async () => {
    const lists = await Promise.all(kinds.map((k) => api.listDevices({ kind: k, limit: 200 })));
    return lists
      .flat()
      .filter((c) => c.Number !== device.Number && c.TenantAlias === device.TenantAlias);
  }, [device.Number, relation]);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isSupply) await api.supplyDevice(device.Number, choice);
      else await api.attachDevice(device.Number, choice);
      onLinked();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isSupply ? "Record the supply" : "Attach to a gateway"}
      hint={
        isSupply
          ? `Only ${allowed.map(kindLabel).join(" and ")} units can feed a ${kindLabel(device.Kind)}`
          : "Commands for this device will be routed through the gateway you choose"
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" form="link-device" disabled={busy || !choice}>
            {busy ? <span className="spinner" /> : null}
            {isSupply ? "Record supply" : "Attach"}
          </button>
        </>
      }
    >
      <form id="link-device" onSubmit={submit}>
        {error && <Callout kind="warning">{error}</Callout>}

        {candidates.loading ? (
          <Loading label="Finding candidates" />
        ) : (candidates.data ?? []).length === 0 ? (
          <Callout kind="warning">
            This tenant has no {kinds.map(kindLabel).join(" or ")} registered yet, so there
            is nothing to link to.
          </Callout>
        ) : (
          <Field
            label={isSupply ? "Fed by" : "Gateway"}
            required
            hint={
              isSupply
                ? "Capacity is taken from the source's model and is refused once it is full."
                : null
            }
          >
            <select
              className="select"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              required
            >
              <option value="">Choose a device…</option>
              {(candidates.data ?? []).map((c) => (
                <option key={c.Number} value={c.Number}>
                  {c.Number} · {kindLabel(c.Kind)} ({c.ModelCode})
                </option>
              ))}
            </select>
          </Field>
        )}
      </form>
    </Modal>
  );
}

function ChannelsTab({ device, channels, isGateway, number }) {
  const children = useAsync(
    () => (isGateway ? api.deviceChildren(number) : Promise.resolve([])),
    [number, isGateway]
  );

  if (isGateway) {
    return (
      <Card
        title="Attached devices"
        hint="Commands for these devices are routed through this gateway"
      >
        {children.loading ? (
          <Loading />
        ) : (children.data ?? []).length === 0 ? (
          <Empty title="Nothing attached">
            No devices report through this gateway yet. Attach one from its own page.
          </Empty>
        ) : (
          <div className="table-wrap" style={{ border: "none" }}>
            <table>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Kind</th>
                  <th>State</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {children.data.map((c) => (
                  <tr key={c.Number}>
                    <td>
                      <Link className="mono" to={`/devices/${c.Number}`}>
                        {c.Number}
                      </Link>
                    </td>
                    <td>{kindLabel(c.Kind)}</td>
                    <td>
                      <StateBadge state={c.State} />
                    </td>
                    <td className="hint">{formatRelative(c.LastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card
      title="Metered channels"
      hint="Each channel has its own number, its own key and its own balance"
    >
      {channels.loading ? (
        <Loading />
      ) : (channels.data ?? []).length === 0 ? (
        <Empty title="No channels">This device exposes no metered circuits.</Empty>
      ) : (
        <div className="table-wrap" style={{ border: "none" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Channel number</th>
                <th>Label</th>
                <th>Power limit</th>
                <th>Customer reference</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {channels.data.map((c) => (
                <tr key={c.Number}>
                  <td>{c.Index}</td>
                  <td className="mono">{c.Number}</td>
                  <td>{c.Label || <span className="hint">Unnamed</span>}</td>
                  <td>{c.PowerLimitWatts ? `${formatNumber(c.PowerLimitWatts, 0)} W` : "Unlimited"}</td>
                  <td className="hint">{c.CustomerRef || "Not linked"}</td>
                  <td>
                    <StateBadge state={c.State} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {device.ChannelCount > 1 && (
        <p className="hint" style={{ marginTop: 12 }}>
          Vend against a specific channel number, not the device number, or the credit will
          land on the wrong circuit.
        </p>
      )}
    </Card>
  );
}
